import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  doc,
  deleteDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser as deleteAuthUser,
} from "firebase/auth";

import { auth, db } from "@/services/firebase";
import defaultDishes from "@/assets/data/default_dishes.json"; // Import JSON directly

// Firestore caps a batch at 500 writes. Everything here operates on a single
// user's dish list, which is far below that today, but chunking keeps the
// admin bulk operations from silently failing on a heavy account.
const BATCH_LIMIT = 500;

const commitInChunks = async (items, addToBatch) => {
  for (let i = 0; i < items.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const item of items.slice(i, i + BATCH_LIMIT)) {
      addToBatch(batch, item);
    }
    await batch.commit();
  }
};

// ==============================
// Dish Operations
// ==============================

// Fetch dishes for the logged-in user
export const listDishes = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const dishesRef = collection(db, "dishes");
    const q = query(dishesRef, where("userId", "==", user.uid));
    const snapshot = await getDocs(q);

    // Ensure the id from Firestore document is preserved by spreading it last
    const dishes = snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));

    return dishes;
  } catch (error) {
    console.error("Failed to fetch dishes:", error.message);
    throw new Error("Failed to fetch dishes");
  }
};

// Add a dish for the logged-in user
export const addDish = async (dish) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const dishesRef = collection(db, "dishes");
    const docRef = await addDoc(dishesRef, {
      ...dish,
      userId: user.uid, // Include authenticated user's UID
      createdAt: new Date().toISOString(),
      LastEaten: null, // Initialize LastEaten field as null
    });

    return { id: docRef.id, ...dish, LastEaten: null };
  } catch (error) {
    console.error("Failed to add dish:", error.message);
    throw new Error("Failed to add dish");
  }
};

// Delete a dish for the logged-in user
export const deleteDish = async (dishId) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const dishRef = doc(db, "dishes", dishId);
    await deleteDoc(dishRef);
  } catch (error) {
    console.error("Failed to delete dish:", error.message);
    throw new Error("Failed to delete dish");
  }
};

// Update an existing dish
export const updateDish = async (dishId, updatedData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const dishRef = doc(db, "dishes", dishId);
    await setDoc(dishRef, updatedData, { merge: true });
  } catch (error) {
    console.error("Failed to update dish:", error.message);
    throw new Error("Failed to update dish.");
  }
};

// Save multiple user dishes in bulk
export const saveUserDishes = async (updatedDishes) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    // One round trip instead of N serial writes, and atomic: a half-saved
    // week would leave LastEaten wrong for the dishes that did land.
    await commitInChunks(updatedDishes, (batch, dish) => {
      batch.set(doc(db, "dishes", dish.id), dish, { merge: true });
    });
  } catch (error) {
    console.error("Failed to save user dishes:", error.message);
    throw new Error("Error updating dishes. Please try again.");
  }
};

// ==============================
// Default/Initial Data
// ==============================

// Initialize user dishes with default dishes
export const initializeUserDishes = async (userId) => {
  try {
    const dishesRef = collection(db, "dishes");
    const createdAt = new Date().toISOString();

    await commitInChunks(defaultDishes, (batch, dish) => {
      batch.set(doc(dishesRef), {
        ...dish,
        userId,
        createdAt,
        LastEaten: null, // Add LastEaten field
      });
    });
  } catch (error) {
    console.error("Failed to initialize user dishes:", error.message);
    throw new Error("Failed to initialize user dishes");
  }
};

// Fetch dishes from default_dishes collection (if you store them in Firestore)
export const fetchDefaultDishes = async () => {
  try {
    const dishesRef = collection(db, "default_dishes");
    const snapshot = await getDocs(dishesRef);

    if (snapshot.empty) {
      return [];
    }

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      LastEaten: null,
    }));
  } catch (error) {
    console.error("Failed to fetch default dishes:", error.message);
    throw new Error("Failed to fetch default dishes");
  }
};

// ==============================
// User Auth & Settings
// ==============================

// Sign in the user
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    console.error("Failed to sign in:", error.message);
    throw new Error("Failed to sign in");
  }
};

// Register a new user and store additional user details
export const register = async (email, password, additionalData = {}) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      email: user.email,
      createdAt: new Date().toISOString(),
      ...additionalData,
    });

    // Best effort: a failure here (rate limiting, mail issues) should not
    // fail an otherwise successful registration - the user can resend from
    // their settings page.
    try {
      await sendEmailVerification(user);
    } catch (error) {
      console.error("Failed to send verification email:", error.message);
    }

    return user;
  } catch (error) {
    console.error("Failed to register user:", error.message);
    throw new Error("Failed to register user");
  }
};

// Resend the verification mail for the signed-in user
export const resendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  if (user.emailVerified) return;
  await sendEmailVerification(user);
};

/**
 * Delete the signed-in user's own account and everything attached to it.
 *
 * Order matters: the Firestore documents have to go first, because once the
 * Auth user is deleted the token that authorises those deletes is gone and the
 * data would be stranded with no owner who can ever reach it. Firebase also
 * requires a recent login before deleting an account, hence the reauth step.
 */
export const deleteOwnAccount = async (password) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);

  const dishesRef = collection(db, "dishes");
  const snapshot = await getDocs(query(dishesRef, where("userId", "==", user.uid)));
  await commitInChunks(snapshot.docs, (batch, dishDoc) => {
    batch.delete(doc(db, "dishes", dishDoc.id));
  });

  await deleteDoc(doc(db, "userSettings", user.uid));
  await deleteDoc(doc(db, "users", user.uid));

  await deleteAuthUser(user);
};

// Save user settings
export const saveUserSettings = async (settings) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const settingsRef = doc(db, "userSettings", user.uid);
    await setDoc(settingsRef, { ...settings });
  } catch (error) {
    console.error("Failed to save user settings:", error.message);
    throw error;
  }
};

// Fetch or create user settings
export const fetchOrCreateUserSettings = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const settingsRef = doc(db, "userSettings", user.uid);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      const existingSettings = settingsDoc.data();

      // Add new fields if they don't exist in the stored settings
      if (!existingSettings.preferenceMode) {
        existingSettings.preferenceMode = "distribution";
      }

      if (!existingSettings.weeklySchedule) {
        existingSettings.weeklySchedule = [
          { mainIngredient: "surprise" }, // Monday
          { mainIngredient: "surprise" }, // Tuesday
          { mainIngredient: "surprise" }, // Wednesday
          { mainIngredient: "surprise" }, // Thursday
          { mainIngredient: "surprise" }, // Friday
          { mainIngredient: "surprise" }, // Saturday
          { mainIngredient: "surprise" }, // Sunday
        ];
      }

      // Return the settings with any newly added fields
      return existingSettings;
    } else {
      // Create default settings if none exist
      const defaultSettings = {
        userId: user.uid, // Include user's UID
        daysBetween: 14,
        preferenceMode: "distribution",
        mainIngredientPreferences: [
          { name: "Fish", days: 3 },
          { name: "Meat", days: 2 },
          { name: "Chicken", days: 1 },
          { name: "Vegetarian", days: 1 },
          { name: "Pork", days: 0 },
          { name: "Seafood", days: 0 },
        ],
        weeklySchedule: [
          { mainIngredient: "surprise" }, // Monday
          { mainIngredient: "surprise" }, // Tuesday
          { mainIngredient: "surprise" }, // Wednesday
          { mainIngredient: "surprise" }, // Thursday
          { mainIngredient: "surprise" }, // Friday
          { mainIngredient: "surprise" }, // Saturday
          { mainIngredient: "surprise" }, // Sunday
        ],
      };

      await setDoc(settingsRef, defaultSettings);
      return defaultSettings;
    }
  } catch (error) {
    console.error("Failed to fetch or create user settings:", error.message);
    throw error;
  }
};

// ==============================
// Admin Functions (New)
// ==============================

/**
 * Fetch all users from the "users" collection in Firestore.
 * Returns an array of user objects with { id, ...data }.
 */
export const fetchAllUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const snapshot = await getDocs(usersRef);

    if (snapshot.empty) {
      return [];
    }

    const allUsers = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    return allUsers;
  } catch (error) {
    console.error("Failed to fetch all users:", error.message);
    throw new Error("Failed to fetch all users");
  }
};

/**
 * Delete all dishes for a specific user (identified by userId).
 */
export const deleteAllDishesForUser = async (userId) => {
  try {
    const dishesRef = collection(db, "dishes");
    const q = query(dishesRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);

    await commitInChunks(snapshot.docs, (batch, dishDoc) => {
      batch.delete(doc(db, "dishes", dishDoc.id));
    });
  } catch (error) {
    console.error("Failed to delete dishes for user:", error.message);
    throw new Error("Failed to delete dishes for user");
  }
};

/**
 * Delete the user’s document in Firestore and optionally:
 *  - Delete their userSettings
 *  - Delete all their dishes
 *  - Remove from Firebase Auth (requires Admin privileges or re-auth flow)
 */
export const deleteUser = async (userId) => {
  try {
    // 1) Delete from "users" collection
    const userRef = doc(db, "users", userId);
    await deleteDoc(userRef);

    // 2) Delete from "userSettings" collection (if exists)
    const settingsRef = doc(db, "userSettings", userId);
    await deleteDoc(settingsRef).catch(() =>
      console.log("No user settings found to delete")
    );

    // 3) Delete all dishes
    await deleteAllDishesForUser(userId);

    // 4) (Optional) Remove user from Firebase Auth
    //    This typically requires elevated privileges
    //    or the Admin SDK on a backend server. Example:
    // await authDeleteUser(userId);
  } catch (error) {
    console.error(`Failed to delete user ${userId}:`, error.message);
    throw new Error("Failed to delete user.");
  }
};

/**
 * Reset the LastEaten field for all dishes belonging to a specific user.
 * Sets LastEaten = null for each dish.
 */
export const resetLastEatenForUser = async (userId) => {
  try {
    const dishesRef = collection(db, "dishes");
    const q = query(dishesRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);

    await commitInChunks(snapshot.docs, (batch, dishDoc) => {
      batch.set(doc(db, "dishes", dishDoc.id), { LastEaten: null }, { merge: true });
    });
  } catch (error) {
    console.error(
      `Failed to reset LastEaten for user ${userId}:`,
      error.message
    );
    throw new Error("Failed to reset LastEaten for user.");
  }
};
